import type { GenerationTraceResponse } from './post';
import { api } from './client';
import {
  AdminUser,
  AdminUsageOverview,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  SchedulerConfig,
} from '@/types';
import type {
  UsageBreakdownResponse,
  TopSpendersResponse,
  UsageEventsResponse,
  UsageEventApiLog,
  XPlatformSettingsResponse,
  XBulkUnsubscribeResponse,
  StyleGuardrailsSettingsResponse,
  StyleGuardrailsViolationsResponse,
  HumanizationPhraseResponse,
  GenerationTraceListResponse,
} from './admin-traces';

export const adminApi = {
  users: (params?: { search?: string; plan?: string; status?: string }) =>
    api.get<AdminUser[]>('/api/admin/users', { params }).then((r) => r.data),
  userDetail: (userId: string) => api.get('/api/admin/users/' + userId).then((r) => r.data),
  // V48 — `updatePlan` and `extendTrial` moved to /api/admin/workspaces/...
  updateStatus: (userId: string, status: string) =>
    api.patch('/api/admin/users/' + userId + '/status', { status }).then((r) => r.data),
  /** Polish brief Step 4.2 — flip system_role between 'admin' and 'user'.
   *  Backend rejects with 409 if demoting the last admin OR if the
   *  user is in `access_status='deleted'` (GDPR-anonymised). */
  updateRole: (userId: string, role: 'admin' | 'user') =>
    api.patch('/api/admin/users/' + userId + '/role', { role }).then((r) => r.data),
  /** Hard-delete an already-anonymised (`access_status='deleted'`)
   *  user from the admin list. Backend 409s if the user isn't already
   *  in the `deleted` state — live deletions still go through the GDPR
   *  flow with its 30-day grace window. */
  hardDeleteAnonymisedUser: (userId: string) =>
    api.delete<{ deleted: boolean }>('/api/admin/users/' + userId).then((r) => r.data),
  forceVerifyEmail: (userId: string) =>
    api.post('/api/admin/users/' + userId + '/verify-email').then((r) => r.data),
  // V113 — admin-provisioned account creation, bypasses the
  // registration_enabled gate. Workspace gets created with
  // plan_override=true + trial_started_at=null when skipOnboarding.
  createUser: (body: {
    email: string;
    name: string;
    password?: string;
    plan?: string;
    skipOnboarding?: boolean;
  }) =>
    api
      .post<{
        id: string;
        email: string;
        name: string | null;
        workspaceId: string | null;
        plan: string;
      }>('/api/admin/users/create', body)
      .then((r) => r.data),
  resendVerification: (userId: string) =>
    api.post('/api/admin/users/' + userId + '/resend-verification').then((r) => r.data),
  usage: () => api.get<AdminUsageOverview>('/api/admin/usage').then((r) => r.data),
  getSettings: () =>
    api
      .get<
        { key: string; value: string; updatedAt: string | null; updatedBy: string | null }[]
      >('/api/admin/settings')
      .then((r) => r.data),
  updateSetting: (key: string, value: string) =>
    api.patch('/api/admin/settings', { key, value }).then((r) => r.data),
  getPlatformHealth: () =>
    api
      .get<{
        jobs: { name: string; lastRun: string | null; status: string }[];
        connectionCounts: Record<string, number>;
        recentErrors: {
          postId: string;
          brandName: string | null;
          platform: string;
          error: string;
          failedAt: string | null;
        }[];
      }>('/api/admin/platform')
      .then((r) => r.data),
  getApiBalances: () =>
    api
      .get<{
        anthropicSpendThisMonth: number;
        falSpendThisMonth: number;
      }>('/api/admin/api-balances')
      .then((r) => r.data),
  getMediaStats: (days?: number, externalOnly?: boolean) =>
    api
      .get<{
        totalRequests: number;
        byType: Record<string, number>;
        byDay: { date: string; total: number; auth: number; external: number }[];
        topKeys: { key: string; type: string; count: number }[];
        uniqueIps: number;
        periodDays: number;
      }>('/api/admin/media/stats', {
        params: { days: days ?? 30, externalOnly: externalOnly ?? false },
      })
      .then((r) => r.data),
  getSchedulers: () => api.get<SchedulerConfig[]>('/api/admin/schedulers').then((r) => r.data),
  updateScheduler: (
    jobKey: string,
    data: { cronExpression?: string; fixedRateMs?: number; enabled?: boolean },
  ) => api.patch<SchedulerConfig>(`/api/admin/schedulers/${jobKey}`, data).then((r) => r.data),
  triggerScheduler: (jobKey: string) =>
    api.post(`/api/admin/schedulers/${jobKey}/trigger`).then((r) => r.data),
  /** Alias for the new SchedulersPage. Backend exposes both `/trigger` and `/run-now`. */
  runScheduler: (jobKey: string) =>
    api.post(`/api/admin/schedulers/${jobKey}/run-now`).then((r) => r.data),
  /** Platform-wide AI defaults — what the admin AI Settings page reads/writes.
   *  AI usage breakdown lives on `usageApi.getAdminAiSummary`. */
  getAiSettings: () =>
    api.get<AdminAiSettingsResponse>('/api/admin/ai-settings').then((r) => r.data),
  updateAiSettings: (data: UpdateAdminAiSettingsRequest) =>
    api.patch<AdminAiSettingsResponse>('/api/admin/ai-settings', data).then((r) => r.data),
  // ── Cost breakdown drill-down (AdminUsageBreakdownController) ──────────
  getUsageBreakdown: (params: {
    workspaceId?: string;
    brandId?: string;
    from?: string;
    to?: string;
  }) =>
    api.get<UsageBreakdownResponse>('/api/admin/usage/breakdown', { params }).then((r) => r.data),
  getUsageTopSpenders: (params: {
    workspaceId?: string;
    brandId?: string;
    from?: string;
    to?: string;
  }) =>
    api.get<TopSpendersResponse>('/api/admin/usage/top-spenders', { params }).then((r) => r.data),
  getUsageEvents: (params: {
    workspaceId?: string;
    brandId?: string;
    from?: string;
    to?: string;
    provider?: string;
    model?: string;
    operationType?: string;
    postId?: string;
    q?: string;
    sortBy?: 'newest' | 'oldest' | 'costDesc' | 'costAsc';
    limit?: number;
    offset?: number;
  }) => api.get<UsageEventsResponse>('/api/admin/usage/events', { params }).then((r) => r.data),
  // Body inspection — find the api_call_log row that matches this AI
  // usage event (proximity match on provider + workspace + ±10s). Used
  // by the admin EventsDrawer "Inspect" expansion to show request +
  // response bodies. 404 = no log within window (retention pruned).
  getUsageEventApiLog: (id: string) =>
    api.get<UsageEventApiLog>(`/api/admin/usage/events/${id}/api-log`).then((r) => r.data),

  // ── Platform settings (per-platform feature toggles) ───────────────────
  // X webhooks toggle is the only one wired today. Disabling it short-
  // circuits XWebhookService.subscribeAccount AND bulk-unsubscribes
  // whatever X currently has registered so the 3-cap pay-per-use limit
  // doesn't keep biting. Falls back to periodic polling (15-min) for
  // engagement when off.
  getXPlatformSettings: () =>
    api.get<XPlatformSettingsResponse>('/api/admin/platform-settings/x').then((r) => r.data),
  updateXPlatformSettings: (enabled: boolean) =>
    api
      .put<XPlatformSettingsResponse>('/api/admin/platform-settings/x', { enabled })
      .then((r) => r.data),
  unsubscribeAllX: () =>
    api
      .post<XBulkUnsubscribeResponse>('/api/admin/platform-settings/x/unsubscribe-all')
      .then((r) => r.data),

  // V161 — public marketing showcase master switch (public_showcase_enabled).
  // On top of this, each workspace opts in individually.
  getShowcaseEnabled: () =>
    api.get<{ enabled: boolean }>('/api/admin/showcase').then((r) => r.data),
  setShowcaseEnabled: (enabled: boolean) =>
    api.put<{ enabled: boolean }>('/api/admin/showcase', { enabled }).then((r) => r.data),

  // ── V146 style guardrails (humanize post generation) ───────────────────
  // Two GETs + one PATCH. The settings panel reads/writes the three flags;
  // the violations panel aggregates the post_style_violations table over
  // the queried window (1..90 days, default 30). Drives /admin/style-guardrails.
  getStyleGuardrailsSettings: () =>
    api
      .get<StyleGuardrailsSettingsResponse>('/api/admin/style-guardrails/settings')
      .then((r) => r.data),
  updateStyleGuardrailsSettings: (patch: Partial<StyleGuardrailsSettingsResponse>) =>
    api
      .patch<StyleGuardrailsSettingsResponse>('/api/admin/style-guardrails/settings', patch)
      .then((r) => r.data),
  getStyleGuardrailsViolations: (days = 30) =>
    api
      .get<StyleGuardrailsViolationsResponse>('/api/admin/style-guardrails/violations', {
        params: { days },
      })
      .then((r) => r.data),

  // ── V159 humanize phrase catalogue ─────────────────────────────────────
  // Admin CRUD for the BANNED_PHRASES + GENERIC_CTAS lists that used to
  // sit hardcoded in HumanizationPostProcessor's companion object. Writes
  // invalidate the runtime cache; the next generation picks up the new
  // catalogue without a redeploy.
  listStyleGuardrailsPhrases: (category: 'banned_phrase' | 'generic_cta', language?: 'en' | 'nl') =>
    api
      .get<HumanizationPhraseResponse[]>('/api/admin/style-guardrails/phrases', {
        params: { category, ...(language ? { language } : {}) },
      })
      .then((r) => r.data),
  addStyleGuardrailsPhrase: (body: {
    category: 'banned_phrase' | 'generic_cta';
    phrase: string;
    language?: 'en' | 'nl';
  }) =>
    api
      .post<HumanizationPhraseResponse>('/api/admin/style-guardrails/phrases', body)
      .then((r) => r.data),
  toggleStyleGuardrailsPhrase: (id: string, enabled: boolean) =>
    api
      .patch<HumanizationPhraseResponse>(`/api/admin/style-guardrails/phrases/${id}`, {
        enabled,
      })
      .then((r) => r.data),
  /**
   * Partial update — only fields present in `patch` change server-side.
   * Server lowercases + trims `phrase`. Collisions with another row
   * (same category + new phrase + new language) return 400 so the admin
   * UI can surface the error inline rather than silently merging rows.
   */
  updateStyleGuardrailsPhrase: (
    id: string,
    patch: { phrase?: string; language?: 'en' | 'nl' | null; enabled?: boolean },
  ) =>
    api
      .patch<HumanizationPhraseResponse>(`/api/admin/style-guardrails/phrases/${id}`, patch)
      .then((r) => r.data),
  deleteStyleGuardrailsPhrase: (id: string) =>
    api.delete(`/api/admin/style-guardrails/phrases/${id}`).then((r) => r.data),

  // ── V147 generation traces (admin global list) ─────────────────────────
  listGenerationTraces: (params: {
    workspaceId?: string;
    brandId?: string;
    status?: string;
    hadViolations?: boolean;
    /**
     * PR 4 — deep-link from /admin/style-guardrails violation rows.
     * Locked to the 5 known HumanizationPostProcessor violation types:
     * em_dash | en_dash | banned_phrase | generic_cta | emoji_bullet.
     * Anything else is silently dropped server-side.
     */
    violationType?: string;
    /** PR 4 — narrow violationType to a specific detail (e.g. "streamline"). */
    violationDetail?: string;
    page?: number;
    pageSize?: number;
  }) =>
    api
      .get<GenerationTraceListResponse>('/api/admin/generation-traces', { params })
      .then((r) => r.data),
  getGenerationTrace: (traceId: string) =>
    api.get<GenerationTraceResponse>(`/api/admin/generation-traces/${traceId}`).then((r) => r.data),
};
